import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { UploadButton } from '@/components/common';
import { ErrorPage } from '@/components/common/ErrorPage';
import { Button, Spinner, Tag } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { queryClient } from '@/configuration/react-query';
import { UPLOAD_PICKER_TYPES } from '@/constants/UploadFileConstants';
import { BlobLocation } from '@/enums/BlobLocationEnum';
import { showToast, showUploadErrorToast, ToastType } from '@/helpers/toast';
import { useFileUpload } from '@/hooks/useFileUpload';
import { TrashIcon } from '@/resources';
import { useGetVideoThemeById, useUpdatePreviewVideoUrl } from '@/services';
import { AssetUploadErrorType, OrganizationBasic } from '@/types';
import { SelectOptionProps } from '@/types/CommonTypes';

import { AssetTagItem } from '../Event/Asset/AssetTagItem';
import { DetailsRow } from '../Organizations/components/DetailsRow';

export const VideoThemeDetail = () => {
  const { themeId } = useParams();
  const [t] = useTranslation('common');

  const [error, setError] = useState<number>();

  const { uploadFileToBlob, isUploading: isUploadingVideo } = useFileUpload({
    pickerTypes: UPLOAD_PICKER_TYPES.VIDEO,
    themeId: Number(themeId),
    blobLocation: BlobLocation.THEMES
  });

  const {
    data: themeData,
    refetch: refetchVideoTheme,
    isLoading: isThemeLoading
  } = useGetVideoThemeById(Number(themeId), {
    onSuccess: () => {
      undefined;
    },
    onError: (error) => {
      setError(error.status);
    }
  });

  const { mutate: updatePreviewVideoUrl } = useUpdatePreviewVideoUrl(queryClient, {
    onSuccess: () => {
      showToast(ToastType.SUCCESS, t('themeUpdated'));
      refetchVideoTheme();
    },
    onError: (error) => {
      showToast(ToastType.ERROR, error.response.data.ErrorMessages?.[0].DisplayMessage);
    }
  });

  const handleUploadPreviewVideo = async (files: File[]) => {
    const file = files[0];
    if (!file) {
      return;
    }

    const result = await uploadFileToBlob(file);

    if (result.error || !result.assetUrl) {
      showUploadErrorToast(result.error?.errorType ?? AssetUploadErrorType.GENERIC_UPLOAD_ERROR);
      return;
    }

    updatePreviewVideoUrl({ id: Number(themeId), previewVideoUrl: result.assetUrl });
  };

  const handleRemovePreviewVideo = () => {
    const payload = {
      id: Number(themeId),
      previewVideoUrl: null
    };
    updatePreviewVideoUrl(payload);
  };

  // TODO: Make config for details rows, after refactoring
  return isThemeLoading ? (
    <Spinner />
  ) : error ? (
    <ErrorPage code={error} />
  ) : (
    <div className='mt-5 w-full overflow-auto md:mt-7 md:px-20'>
      <div className='flex flex-col gap-y-3 md:gap-y-4'>
        <DetailsRow label={t('themeName')} value={themeData?.data.name} labelWidth='md:w-1/5' />
        <DetailsRow
          label={t('musicStartDelay')}
          value={t('timeInSeconds', {
            value: themeData?.data.musicStartDelay ?? 0
          })}
          labelWidth='md:w-1/5'
        />
        <DetailsRow
          label={t('musicEndDelay')}
          value={t('timeInSeconds', {
            value: themeData?.data.musicEndDelay ?? 0
          })}
          labelWidth='md:w-1/5'
        />
        <DetailsRow
          label={t('barsColor')}
          value={
            <div
              className='size-5 rounded-full border border-gray-300 md:justify-start'
              style={{
                backgroundColor: '#' + themeData?.data.barsColor || 'var(--color-primary-white)'
              }}
            />
          }
          labelWidth='md:w-1/5'
        />
        <DetailsRow label={t('order')} value={themeData?.data.order} labelWidth='md:w-1/5' />
        {themeData?.data.organizations?.length > 0 && (
          <DetailsRow
            label={t('organizations')}
            value={themeData?.data.organizations
              .map((org: OrganizationBasic) => org.name)
              .join(', ')}
            labelWidth='md:w-1/5'
          />
        )}
        <DetailsRow
          label={t('status')}
          value={<Tag value={`${themeData?.data.isPublish ? 'Published' : 'Drafted'}`} />}
          labelWidth='md:w-1/5'
        />
        <DetailsRow
          label={t('tags')}
          value={
            <div className='flex flex-wrap gap-1'>
              {themeData?.data.tags?.map((tag: string) => (
                <AssetTagItem
                  key={tag}
                  tag={{ label: tag, value: tag } as SelectOptionProps}
                  className='!mx-0 !mr-1'
                  isRemovable={false}
                />
              ))}
            </div>
          }
          labelWidth='md:w-1/5'
        />
        <DetailsRow
          label={t('description')}
          value={themeData?.data.description}
          variant='stacked'
        />
        <DetailsRow
          label={t('previewVideo')}
          value={
            themeData?.data.previewVideoUrl ? (
              <div className='flex flex-col gap-2'>
                <video
                  src={themeData.data.previewVideoUrl}
                  controls
                  className='mb-2 mt-4 h-48 w-full rounded-lg border border-secondary-blue-gray object-cover md:w-1/3'
                />
                <div className='md:w-1/3'>
                  <Button
                    variant={ButtonVariant.SECONDARY}
                    handleOnClick={handleRemovePreviewVideo}
                    className='flex w-full items-center gap-2'>
                    <TrashIcon className='size-4 text-primary-blue' />
                    {t('deletePreviewVideo')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className='flex flex-col gap-2'>
                <span className='text-sm text-accent-dark-gray'>{t('noPreviewVideo')}</span>
                <UploadButton
                  multiple={false}
                  text='uploadPreviewVideo'
                  pickerTypes={UPLOAD_PICKER_TYPES.VIDEO}
                  showMaxFileSize={false}
                  isUploading={isUploadingVideo}
                  onFilesSelected={handleUploadPreviewVideo}
                  variant={ButtonVariant.QUATERNARY}
                />
              </div>
            )
          }
          variant='stacked'
          labelWidth='md:w-1/5'
        />
      </div>
    </div>
  );
};
